const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// New standardized API response interfaces (Phase 5)
export interface StandardApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface StandardApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

// Legacy error interface for backwards compatibility
export interface ApiError {
  error: string;
  message: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Global auth callback - will be set by AuthContext
let globalAuthErrorCallback: (() => void) | null = null;

export const setAuthErrorCallback = (callback: () => void) => {
  globalAuthErrorCallback = callback;
};

export class BaseService {
  protected getAuthHeader(): string | null {
    const token = localStorage.getItem('access_token');
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Make a request and return the full standardized response
   * Use this when you need access to pagination, messages, etc.
   */
  protected async makeFullRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<StandardApiResponse<T>> {
    const authHeader = this.getAuthHeader();
    
    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader && { Authorization: authHeader }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      // Handle errors the same way as makeRequest
      let errorData: StandardApiError | ApiError;
      try {
        const jsonError = await response.json();
        if (jsonError.success === false && jsonError.error) {
          errorData = jsonError as StandardApiError;
        } else {
          errorData = jsonError as ApiError;
        }
      } catch {
        errorData = {
          error: 'Unknown Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        } as ApiError;
      }
      
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        url: `${API_BASE_URL}/api${endpoint}`,
        errorData
      });

      if (response.status === 401) {
        console.warn('Authentication token invalid - triggering logout');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (globalAuthErrorCallback) {
          globalAuthErrorCallback();
        }
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = 'error' in errorData && typeof errorData.error === 'object' 
        ? errorData.error.message 
        : (errorData as ApiError).message || `HTTP ${response.status}: ${response.statusText}`;
      
      throw new Error(errorMessage);
    }

    return response.json() as Promise<StandardApiResponse<T>>;
  }

  protected async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const authHeader = this.getAuthHeader();
    const url = `${API_BASE_URL}/api${endpoint}`;
    
    // Debug log the request
    if (endpoint.includes('/jobs/batch')) {
      console.log('Sending request to:', url);
      console.log('Request options:', options);
      if (options.body) {
        try {
          console.log('Request body:', JSON.parse(options.body as string));
        } catch (e) {
          console.log('Request body (raw):', options.body);
        }
      }
    }
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader && { Authorization: authHeader }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      // Try to parse as standardized error format first
      let errorData: StandardApiError | ApiError;
      try {
        const jsonError = await response.json();
        
        // Check if it's the new standardized format
        if (jsonError.success === false && jsonError.error) {
          errorData = jsonError as StandardApiError;
        } else {
          // Fall back to legacy format
          errorData = jsonError as ApiError;
        }
      } catch {
        // If JSON parsing fails, create a basic error
        errorData = {
          error: 'Unknown Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        } as ApiError;
      }
      
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        url: `${API_BASE_URL}/api${endpoint}`,
        errorData
      });

      // Handle 401 Unauthorized - token is invalid/expired
      if (response.status === 401) {
        console.warn('Authentication token invalid - triggering logout');
        
        // Clear tokens immediately
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        // Trigger auth error callback to redirect to login
        if (globalAuthErrorCallback) {
          globalAuthErrorCallback();
        }
        
        // Throw a specific auth error
        throw new Error('Authentication expired. Please log in again.');
      }
      
      // Extract error message from either format
      const errorMessage = 'error' in errorData && typeof errorData.error === 'object' 
        ? errorData.error.message 
        : (errorData as ApiError).message || `HTTP ${response.status}: ${response.statusText}`;
      
      throw new Error(errorMessage);
    }

    // Handle empty responses (e.g., 204 No Content or empty body)
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type');

    if (response.status === 204 || contentLength === '0' || !contentType?.includes('application/json')) {
      // Return undefined for empty responses
      return undefined as T;
    }

    // Try to parse response text first to check if it's empty
    const text = await response.text();
    if (!text || text.trim() === '') {
      return undefined as T;
    }

    // Parse the response and handle standardized format
    const jsonResponse = JSON.parse(text);

    // Check if it's the new standardized format
    if (jsonResponse.success !== undefined && jsonResponse.data !== undefined) {
      // Return just the data portion for backwards compatibility
      return jsonResponse.data as T;
    }

    // Return as-is for legacy responses
    return jsonResponse as T;
  }
}